import { getAbsoluteEndpoint, getChunksPath } from '../apiRouting'
import {
  ApiError,
  buildProtectedAuthHeaders,
  fetchWithTimeout,
  readApiError,
  throwIfAuthRejected,
  throwIfSubmissionRejected,
} from './client'

export interface ChunkPresignResponse {
  sessionId: string
  chunkIndex: number
  objectKey: string
  presignedUrl: string
  expiresAt: string
}

export interface FinalizeSessionResponse {
  submissionId: string
  syncedAt: string
  processingStatus: string
}

export interface ChunkSessionStatusResponse {
  sessionId: string
  receivedChunks: number
  totalChunks: number
  finalized: boolean
  complete: boolean
  partial: boolean
}

function getChunksEndpoint(pathSuffix: string): string {
  const chunksBase = getAbsoluteEndpoint('chunksWrite', getChunksPath)
  return `${chunksBase}${pathSuffix}`
}

export async function getPresignedChunkUrl(
  sessionId: string,
  chunkIndex: number,
  totalChunks: number,
  audioMimeType: string = 'audio/webm',
): Promise<ChunkPresignResponse> {
  const headers = await buildProtectedAuthHeaders('presign chunk', {
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTimeout(getChunksEndpoint('/presign'), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      sessionId,
      chunkIndex,
      totalChunks,
      audioMimeType,
    }),
  }, 45_000)

  if (!response.ok) {
    throwIfAuthRejected(response, 'presign chunk')
    const error: ApiError = await readApiError(response)
    throw new Error(error.message || error.error || 'Failed to get presigned chunk URL')
  }

  return response.json()
}

export async function finalizeChunkSession(
  sessionId: string,
  submissionId: string,
  metadata: {
    totalChunks: number
    assetId: string
    indicationId: string
    kiqIds: string[]
    insightTags?: string[]
    createdAt: string
    audioMimeType?: string
    durationMs?: number
    participants?: unknown[]
    recordingWithoutParticipants?: boolean
    consentDescription?: string
    consentConfirmed?: boolean
  },
): Promise<FinalizeSessionResponse> {
  const headers = await buildProtectedAuthHeaders('finalize chunk session', {
    'Content-Type': 'application/json',
    'Idempotency-Key': submissionId,
  })

  const response = await fetchWithTimeout(getChunksEndpoint('/finalize-session'), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      sessionId,
      submissionId,
      ...metadata,
    }),
  }, 60_000)

  if (!response.ok) {
    const error: ApiError = await readApiError(response)
    throwIfSubmissionRejected(response, error)
    throwIfAuthRejected(response, 'finalize chunk session')
    throw new Error(error.message || error.error || 'Failed to finalize chunk session')
  }

  return response.json()
}

export async function getChunkSessionStatus(
  sessionId: string,
): Promise<ChunkSessionStatusResponse> {
  const headers = await buildProtectedAuthHeaders('chunk session status', {
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTimeout(
    getChunksEndpoint(`/session/${encodeURIComponent(sessionId)}/status`),
    { method: 'GET', headers },
    15_000,
  )

  if (!response.ok) {
    throwIfAuthRejected(response, 'chunk session status')
    const error: ApiError = await readApiError(response)
    throw new Error(error.message || error.error || 'Failed to get chunk session status')
  }

  return response.json()
}

export async function reStitchSession(
  sessionId: string,
  submissionId: string,
): Promise<{ submissionId: string; processingStatus: string }> {
  const headers = await buildProtectedAuthHeaders('re-stitch session', {
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTimeout(getChunksEndpoint('/re-stitch'), {
    method: 'POST',
    headers,
    body: JSON.stringify({ sessionId, submissionId }),
  }, 60_000)

  if (!response.ok) {
    throwIfAuthRejected(response, 're-stitch session')
    const error: ApiError = await readApiError(response)
    throw new Error(error.message || error.error || 'Failed to re-stitch session')
  }

  return response.json()
}
